import {
  Component,
  DestroyRef,
  ViewEncapsulation,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ButtonModule } from '@components/buttons/button/button.module';
import { ConfirmDialogComponent } from '@components/confirm-dialog/confirm-dialog.component';
import { DatePickerComponent } from '@components/date-picker/date-picker.component';
import { DialogHeaderComponent } from '@components/dialog-header/dialog-header.component';
import { filter, forkJoin, of, switchMap } from 'rxjs';
import { formatVehicleDialogContext } from '../../../core/utils/vehicle-dialog-title.util';
import { FileUrlService } from '../../../services/file-url.service';
import { InternalFileUploadService } from '../../../services/internal-file-upload.service';
import { NotificationService } from '../../../services/notification.service';
import { VehicleService } from '../../../services/vehicle.service';
import { confirmActiveDateIfNotThursday } from '../../../utils/active-date.util';
import { toLocalDateKey } from '../../../utils/local-date.util';
import { DateTypes } from '../../../utils/vehicle-date-types';
import { FileDropZoneComponent } from '../../uploads/file-drop-zone/file-drop-zone.component';
import {
  dateKeyToDate,
  formatDateKey,
  isCanadaRegion,
  liveDateFromActive,
  storedTranslationDateOf,
  translationDateFromActive,
  translationOverrideToPersist
} from '../dashboard-dates.util';
import type { DashboardVehicleTile, OrderGuideAttachment } from '../dashboard.types';
import type { OrderGuideNote } from '../order-guide-note.util';
import { OrderGuideNotesSectionComponent } from '../order-guide-notes-section/order-guide-notes-section.component';
import { registerUnsavedChanges } from '../../../core/unsaved-changes/unsaved-changes.service';
import { OrderGuideNotesStore } from '../order-guide-notes.store';

export interface OrderGuideTileDialogData {
  tile: DashboardVehicleTile;
}

/**
 * Dates reload the favorites query; notes are staged here and persisted by the dashboard on close.
 * Attachments are uploaded/unlinked live against the store, so the dashboard picks them up reactively.
 */
export type OrderGuideTileDialogResult = {
  datesChanged: boolean;
  notes?: OrderGuideNote[];
};

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

@Component({
  selector: 'app-order-guide-tile-dialog',
  encapsulation: ViewEncapsulation.None,
  imports: [
    ButtonModule,
    DatePickerComponent,
    DialogHeaderComponent,
    FileDropZoneComponent,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatTooltipModule,
    OrderGuideNotesSectionComponent,
    ReactiveFormsModule
  ],
  templateUrl: './order-guide-tile-dialog.component.html',
  styleUrl: './order-guide-tile-dialog.component.scss'
})
export class OrderGuideTileDialogComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef =
    inject<MatDialogRef<OrderGuideTileDialogComponent, OrderGuideTileDialogResult>>(MatDialogRef);
  private readonly matDialog = inject(MatDialog);
  private readonly vehicleService = inject(VehicleService);
  private readonly notify = inject(NotificationService);
  private readonly internalUpload = inject(InternalFileUploadService);
  private readonly fileUrls = inject(FileUrlService);
  private readonly notesStore = inject(OrderGuideNotesStore);
  readonly data = inject<OrderGuideTileDialogData>(MAT_DIALOG_DATA);

  readonly form = this.formBuilder.group({
    activeDate: new FormControl<Date | null>(dateKeyToDate(this.data.tile.activeDate)),
    translationDate: new FormControl<Date | null>(dateKeyToDate(this.data.tile.translationDate)),
    sorpDate: new FormControl<Date | null>(dateKeyToDate(this.data.tile.sorpDate))
  });

  readonly notes = signal<OrderGuideNote[]>([...this.data.tile.notes]);
  readonly attachments = signal<OrderGuideAttachment[]>([...this.data.tile.attachments]);
  readonly attachmentsExpanded = signal(true);
  readonly attachmentAddVisible = signal(false);
  readonly saving = signal(false);
  readonly uploading = signal(false);
  /** Bumps when the date form changes so `hasChanges` re-evaluates. */
  private readonly formRevision = signal(0);
  private readonly notesSection = viewChild(OrderGuideNotesSectionComponent);
  private discardPromptOpen = false;

  /** True while Translation still matches the formula, so Active Date edits keep driving it. */
  private translationFollowsDefault = this.computeTranslationFollowsDefault();

  constructor() {
    registerUnsavedChanges(this, () => this.hasUnsavedWork());
    this.dialogRef.disableClose = true;
    this.dialogRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onCancel());
    this.dialogRef
      .keydownEvents()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => event.key === 'Escape')
      )
      .subscribe(() => this.onCancel());
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.formRevision.update((n) => n + 1));
    this.bindTranslationDefault();
  }

  get dialogTitle(): string {
    return `Order Guide - ${formatVehicleDialogContext(this.data.tile.vehicle)}`;
  }

  get liveDateDisplay(): string {
    return formatDateKey(liveDateFromActive(this.activeDateKey));
  }

  get showTranslationDate(): boolean {
    return isCanadaRegion(this.data.tile.vehicle.regionName);
  }

  /** True when dates or notes differ from what the dialog opened with. */
  get hasChanges(): boolean {
    this.formRevision();
    this.notes();
    return this.datesHaveChanged() || this.notesHaveChanged();
  }

  /** Live Date recomputes from this as the picker changes. */
  private get activeDateKey(): string | null {
    const value = this.form.controls.activeDate.value;
    return value ? toLocalDateKey(new Date(value)) : null;
  }

  getControl(path: string): FormControl {
    return this.form.get(path) as FormControl;
  }

  onNotesChange(notes: OrderGuideNote[]): void {
    this.notes.set(notes);
  }

  toggleAttachments(): void {
    this.attachmentsExpanded.update((open) => !open);
  }

  toggleAttachmentAdd(event: Event): void {
    event.stopPropagation();
    if (this.attachmentAddVisible()) {
      this.attachmentAddVisible.set(false);
      return;
    }
    this.attachmentsExpanded.set(true);
    this.attachmentAddVisible.set(true);
  }

  onCancel(): void {
    if (this.saving()) {
      return;
    }
    if (!this.hasUnsavedWork()) {
      this.closeWithoutSaving();
      return;
    }
    this.confirmDiscard();
  }

  /** Dates, staged notes, or typed-but-not-added note text that would be lost on close. */
  private hasUnsavedWork(): boolean {
    return this.hasChanges || (this.notesSection()?.hasUnsavedDraft() ?? false);
  }

  private confirmDiscard(): void {
    if (this.discardPromptOpen) {
      return;
    }
    this.discardPromptOpen = true;
    this.matDialog
      .open(ConfirmDialogComponent, {
        width: '480px',
        data: {
          message: 'Are you sure you want to discard your changes?',
          consequenceMessage: 'Notes and date changes will not be saved.',
          variant: 'warning',
          confirmLabel: 'Discard',
          cancelLabel: 'Keep editing'
        }
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        this.discardPromptOpen = false;
        if (confirmed === true) {
          this.closeWithoutSaving();
        }
      });
  }

  private closeWithoutSaving(): void {
    this.dialogRef.close({ datesChanged: false });
  }

  onSave(): void {
    if (!this.hasChanges || this.saving()) {
      return;
    }

    const vehicleId = this.data.tile.vehicle.id;
    const raw = this.form.getRawValue();
    const activeKey = raw.activeDate ? toLocalDateKey(raw.activeDate) : null;
    const sorpKey = raw.sorpDate ? toLocalDateKey(raw.sorpDate) : null;
    const storedTranslation = storedTranslationDateOf(this.data.tile.vehicle.dates);
    const translationOverrideKey = this.showTranslationDate
      ? translationOverrideToPersist(
          raw.translationDate ? toLocalDateKey(raw.translationDate) : null,
          activeKey
        )
      : storedTranslation;

    const activeChanged = activeKey !== this.data.tile.activeDate;
    const sorpChanged = sorpKey !== this.data.tile.sorpDate;
    const translationChanged =
      this.showTranslationDate && translationOverrideKey !== storedTranslation;

    const notesResult = this.notesHaveChanged() ? this.notes() : undefined;
    const datesChanged = activeChanged || sorpChanged || translationChanged;

    if (!datesChanged) {
      this.dialogRef.close({
        datesChanged: false,
        notes: notesResult
      });
      return;
    }

    const activeDateCheck$ = activeChanged
      ? confirmActiveDateIfNotThursday(this.matDialog, raw.activeDate)
      : of(true);

    activeDateCheck$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((confirmed) => {
          if (!confirmed) {
            return of(null);
          }
          this.saving.set(true);
          return forkJoin({
            active: activeChanged
              ? this.vehicleService.upsertVehicleDate(vehicleId, DateTypes.Publish, raw.activeDate)
              : of(null),
            translation: translationChanged
              ? this.vehicleService.upsertVehicleDate(
                  vehicleId,
                  DateTypes.Translation,
                  dateKeyToDate(translationOverrideKey)
                )
              : of(null),
            sorp: sorpChanged
              ? this.vehicleService.upsertVehicleDate(vehicleId, DateTypes.Sorp, raw.sorpDate)
              : of(null)
          });
        })
      )
      .subscribe({
        next: (result) => {
          if (result == null) {
            return;
          }
          this.saving.set(false);
          this.notify.success('Order guide dates saved');
          this.dialogRef.close({ datesChanged: true, notes: notesResult });
        },
        error: (err: Error) => {
          this.saving.set(false);
          this.notify.error(err.message);
        }
      });
  }

  /** Link for opening an attachment inline (served from the internal container over REST). */
  attachmentUrl(file: OrderGuideAttachment): string {
    return this.fileUrls.getInternalFileUrl(file.id);
  }

  /**
   * Uploads happen immediately: the bytes and the vehicle link are persisted server-side, then this
   * vehicle's attachments are refreshed from the store so both the dialog and dashboard reflect them.
   */
  onAddAttachments(files?: globalThis.File[]): void {
    const picked = (files ?? []).filter((f) => {
      if (f.size > MAX_ATTACHMENT_BYTES) {
        this.notify.error(`${f.name} is larger than 10 MB and was skipped.`);
        return false;
      }
      return true;
    });
    if (!picked.length) {
      return;
    }

    const vehicleId = this.data.tile.vehicle.id;
    this.uploading.set(true);
    forkJoin(picked.map((file) => this.internalUpload.upload(file, vehicleId)))
      .pipe(
        switchMap(() => this.notesStore.reloadFilesForVehicle(vehicleId)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.attachments.set(this.notesStore.filesFor(vehicleId));
          this.attachmentsExpanded.set(true);
          this.attachmentAddVisible.set(true);
          this.uploading.set(false);
          this.notify.success(picked.length === 1 ? 'Attachment uploaded' : 'Attachments uploaded');
        },
        error: (err: Error) => {
          this.uploading.set(false);
          this.notify.error(err.message);
        }
      });
  }

  onRemoveAttachment(file: OrderGuideAttachment): void {
    const vehicleId = this.data.tile.vehicle.id;
    this.notesStore
      .unlinkAttachment(vehicleId, file.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.attachments.set(this.notesStore.filesFor(vehicleId));
          this.notify.success('Attachment removed');
        },
        error: (err: Error) => this.notify.error(err.message)
      });
  }

  private computeTranslationFollowsDefault(): boolean {
    const stored = storedTranslationDateOf(this.data.tile.vehicle.dates);
    const formula = translationDateFromActive(this.data.tile.activeDate);
    return stored == null || stored === formula;
  }

  private datesHaveChanged(): boolean {
    const raw = this.form.getRawValue();
    const activeKey = raw.activeDate ? toLocalDateKey(raw.activeDate) : null;
    const sorpKey = raw.sorpDate ? toLocalDateKey(raw.sorpDate) : null;
    if (activeKey !== this.data.tile.activeDate || sorpKey !== this.data.tile.sorpDate) {
      return true;
    }
    if (!this.showTranslationDate) {
      return false;
    }
    const storedTranslation = storedTranslationDateOf(this.data.tile.vehicle.dates);
    const translationOverrideKey = translationOverrideToPersist(
      raw.translationDate ? toLocalDateKey(raw.translationDate) : null,
      activeKey
    );
    return translationOverrideKey !== storedTranslation;
  }

  private notesHaveChanged(): boolean {
    const initial = this.data.tile.notes;
    const current = this.notes();
    if (initial.length !== current.length) {
      return true;
    }
    const byId = new Map(initial.map((n) => [n.id, n]));
    for (const note of current) {
      const prior = byId.get(note.id);
      if (prior == null || prior.text !== note.text) {
        return true;
      }
    }
    return false;
  }

  private bindTranslationDefault(): void {
    if (!this.showTranslationDate) {
      return;
    }

    this.form.controls.activeDate.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.translationFollowsDefault) {
          return;
        }
        this.form.controls.translationDate.setValue(
          dateKeyToDate(translationDateFromActive(this.activeDateKey)),
          { emitEvent: false }
        );
      });

    this.form.controls.translationDate.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const current = value ? toLocalDateKey(new Date(value)) : null;
        const formula = translationDateFromActive(this.activeDateKey);
        this.translationFollowsDefault = current == null || current === formula;
      });
  }
}
